import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { fetchMySessions, deleteSession, renameSession } from '../../api/sessions';
import { useCertStore } from '../../stores/certStore';
import type { SessionSummary } from '../../types';
import { Button, Card, TextField } from '../ui';

type ModeFilter = 'all' | 'study' | 'exam';
type StatusFilter = 'all' | 'active' | 'completed';
type SortKey = 'date' | 'score' | 'questions';

const PAGE_SIZE = 20;

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return remainMins > 0 ? `${hours}h ${remainMins}m` : `${hours}h`;
}

function formatDate(dateStr: string, locale: string): string {
  return new Date(dateStr).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function SessionHistoryTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const certCode = useCertStore((s) => s.certCode);
  const dateLocale = t('dashboard.study.history.dateLocale');

  const modeOptionLabels: Record<string, string> = {
    all: t('dashboard.study.history.modeAll'),
    study: t('dashboard.study.history.modeStudy'),
    exam: t('dashboard.study.history.modeExam'),
  };
  const statusOptionLabels: Record<string, string> = {
    all: t('dashboard.study.history.statusAll'),
    active: t('dashboard.study.history.statusActive'),
    completed: t('dashboard.study.history.statusCompleted'),
  };

  const [modeFilter, setModeFilter] = useState<ModeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [offset, setOffset] = useState(0);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['my-sessions', certCode, 'history', modeFilter, statusFilter, offset],
    queryFn: () =>
      fetchMySessions({
        mode: modeFilter === 'all' ? undefined : modeFilter,
        status: statusFilter,
        limit: PAGE_SIZE,
        offset,
      }, certCode),
    staleTime: 30_000,
  });

  const deleteMut = useMutation({
    mutationFn: deleteSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-sessions'] });
      setConfirmDeleteId(null);
    },
  });

  const renameMut = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => renameSession(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-sessions'] });
      setEditingId(null);
    },
  });

  const handleFilterChange = useCallback(
    (setter: (v: never) => void, value: never) => {
      setter(value);
      setOffset(0);
    },
    [],
  );

  // Sort items client-side
  const sortedItems = [...(data?.items ?? [])].sort((a, b) => {
    switch (sortKey) {
      case 'score': {
        const aScore = a.total_answered > 0 ? a.correct_count / a.total_answered : 0;
        const bScore = b.total_answered > 0 ? b.correct_count / b.total_answered : 0;
        return bScore - aScore;
      }
      case 'questions':
        return b.total_answered - a.total_answered;
      case 'date':
      default:
        return new Date(b.started_at).getTime() - new Date(a.started_at).getTime();
    }
  });

  const hasMore = data ? offset + PAGE_SIZE < data.total : false;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <FilterPills
          label={t('dashboard.study.history.modeLabel')}
          options={['all', 'study', 'exam']}
          optionLabels={modeOptionLabels}
          value={modeFilter}
          onChange={(v) => handleFilterChange(setModeFilter as never, v as never)}
        />
        <FilterPills
          label={t('dashboard.study.history.statusLabel')}
          options={['all', 'active', 'completed']}
          optionLabels={statusOptionLabels}
          value={statusFilter}
          onChange={(v) => handleFilterChange(setStatusFilter as never, v as never)}
        />
        <div className="ml-auto flex items-center gap-2">
          <label htmlFor="sort-select" className="text-xs text-slate-500 dark:text-slate-400">
            {t('dashboard.study.history.sortLabel')}
          </label>
          <select
            id="sort-select"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            className="rounded-xl border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:border-blue-600 focus:outline-none focus:ring-[3px] focus:ring-blue-600/15 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          >
            <option value="date">{t('dashboard.study.history.sortDate')}</option>
            <option value="score">{t('dashboard.study.history.sortScore')}</option>
            <option value="questions">{t('dashboard.study.history.sortQuestions')}</option>
          </select>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent dark:border-blue-400 dark:border-t-transparent" />
        </div>
      )}

      {/* Empty */}
      {!isLoading && sortedItems.length === 0 && (
        <Card className="py-16 text-center">
          <p className="text-lg text-slate-500 dark:text-slate-400">{t('dashboard.study.history.emptyTitle')}</p>
          <p className="mt-1 text-sm text-slate-400 dark:text-slate-500">
            {t('dashboard.study.history.emptyDescription')}
          </p>
        </Card>
      )}

      {/* Desktop table */}
      {sortedItems.length > 0 && (
        <>
          {/* Desktop */}
          <Card className="hidden overflow-hidden p-0 md:block">
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50/60 dark:border-white/10 dark:bg-white/5">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('dashboard.study.history.colSession')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('dashboard.study.history.colMode')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('dashboard.study.history.colStatus')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('dashboard.study.history.colProgress')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('dashboard.study.history.colScore')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('dashboard.study.history.colDuration')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('dashboard.study.history.colDate')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('dashboard.study.history.colActions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-white/5">
                {sortedItems.map((s) => (
                  <HistoryRow
                    key={s.id}
                    session={s}
                    t={t}
                    dateLocale={dateLocale}
                    editingId={editingId}
                    editName={editName}
                    confirmDeleteId={confirmDeleteId}
                    onStartEdit={(id, name) => {
                      setEditingId(id);
                      setEditName(name);
                    }}
                    onEditNameChange={setEditName}
                    onSaveEdit={() => renameMut.mutate({ id: editingId!, name: editName })}
                    onCancelEdit={() => setEditingId(null)}
                    onResume={(id) => navigate(`/study/session/${id}`)}
                    onConfirmDelete={setConfirmDeleteId}
                    onDelete={(id) => deleteMut.mutate(id)}
                    onCancelDelete={() => setConfirmDeleteId(null)}
                    onReview={(id) => navigate(`/exam/results/${id}`)}
                  />
                ))}
              </tbody>
            </table>
            </div>
          </Card>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {sortedItems.map((s) => (
              <MobileHistoryCard
                key={s.id}
                session={s}
                t={t}
                dateLocale={dateLocale}
                onResume={() => navigate(`/study/session/${s.id}`)}
                onDelete={() => deleteMut.mutate(s.id)}
                onReview={() => navigate(`/exam/results/${s.id}`)}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('dashboard.study.history.showingRange', { from: offset + 1, to: Math.min(offset + PAGE_SIZE, data?.total ?? 0), total: data?.total ?? 0 })}
            </p>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
              >
                {t('dashboard.study.history.previous')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={!hasMore}
              >
                {t('dashboard.study.history.next')}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function FilterPills({
  label,
  options,
  optionLabels,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  optionLabels?: Record<string, string>;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}:</span>
      {options.map((opt) => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          className={`rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors
            ${value === opt
              ? 'bg-blue-600 text-white shadow-[0_4px_10px_-4px_rgba(37,99,235,0.5)]'
              : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-400 dark:hover:bg-white/10'
            }`}
        >
          {optionLabels?.[opt] ?? opt}
        </button>
      ))}
    </div>
  );
}

function HistoryRow({
  session: s,
  t,
  dateLocale,
  editingId,
  editName,
  confirmDeleteId,
  onStartEdit,
  onEditNameChange,
  onSaveEdit,
  onCancelEdit,
  onResume,
  onConfirmDelete,
  onDelete,
  onCancelDelete,
  onReview,
}: {
  session: SessionSummary;
  t: TFunction;
  dateLocale: string;
  editingId: number | null;
  editName: string;
  confirmDeleteId: number | null;
  onStartEdit: (id: number, name: string) => void;
  onEditNameChange: (name: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onResume: (id: number) => void;
  onConfirmDelete: (id: number | null) => void;
  onDelete: (id: number) => void;
  onCancelDelete: () => void;
  onReview: (id: number) => void;
}) {
  const accuracy =
    s.total_answered > 0 ? Math.round((s.correct_count / s.total_answered) * 100) : 0;
  const progress =
    s.total_questions > 0
      ? `${s.total_answered}/${s.total_questions}`
      : `${s.total_answered}`;

  return (
    <tr className="transition-colors hover:bg-slate-50 dark:hover:bg-white/5">
      <td className="px-3 py-2.5">
        {editingId === s.id ? (
          <form
            className="flex items-center gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              onSaveEdit();
            }}
          >
            <TextField
              autoFocus
              value={editName}
              onChange={(e) => onEditNameChange(e.target.value)}
              wrapperClassName="w-40"
              className="!py-0.5 !text-xs"
            />
            <button type="submit" className="text-xs text-blue-600 dark:text-blue-400">
              {t('dashboard.study.history.save')}
            </button>
            <button type="button" onClick={onCancelEdit} className="text-xs text-slate-400 dark:text-slate-500">
              {t('dashboard.study.history.cancel')}
            </button>
          </form>
        ) : (
          <button
            onClick={() => onStartEdit(s.id, s.session_name)}
            className="max-w-[200px] truncate font-medium text-slate-900 hover:text-blue-600 dark:text-white dark:hover:text-blue-400"
            title={s.session_name}
          >
            {s.session_name}
          </button>
        )}
      </td>
      <td className="px-3 py-2.5">
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            s.mode === 'exam'
              ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
              : 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'
          }`}
        >
          {s.mode === 'exam' ? t('dashboard.study.history.badgeExam') : t('dashboard.study.history.badgeStudy')}
        </span>
      </td>
      <td className="px-3 py-2.5">
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            s.is_completed
              ? 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300'
              : 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
          }`}
        >
          {s.is_completed ? t('dashboard.study.history.statusCompletedLabel') : t('dashboard.study.history.statusActiveLabel')}
        </span>
      </td>
      <td className="px-3 py-2.5 text-slate-700 dark:text-slate-300">{progress}</td>
      <td className="px-3 py-2.5">
        <span
          className={`font-semibold ${
            accuracy >= 70 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
          }`}
        >
          {s.total_answered > 0 ? `${accuracy}%` : '-'}
        </span>
      </td>
      <td className="px-3 py-2.5 text-slate-500 dark:text-slate-400">
        {s.time_spent_seconds > 0 ? formatDuration(s.time_spent_seconds) : '-'}
      </td>
      <td className="px-3 py-2.5 text-slate-500 dark:text-slate-400">
        {formatDate(s.started_at, dateLocale)}
      </td>
      <td className="px-3 py-2.5 text-right">
        <div className="flex items-center justify-end gap-2">
          {!s.is_completed && (
            <button
              onClick={() => onResume(s.id)}
              className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              {t('dashboard.study.history.resume')}
            </button>
          )}
          {s.is_completed && (
            <button
              onClick={() => onReview(s.id)}
              className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              {t('dashboard.study.history.review')}
            </button>
          )}
          {confirmDeleteId === s.id ? (
            <span className="flex gap-1">
              <button
                onClick={() => onDelete(s.id)}
                className="text-xs font-medium text-red-600 dark:text-red-400"
              >
                {t('dashboard.study.history.confirm')}
              </button>
              <button onClick={onCancelDelete} className="text-xs text-slate-400 dark:text-slate-500">
                {t('dashboard.study.history.cancel')}
              </button>
            </span>
          ) : (
            <button
              onClick={() => onConfirmDelete(s.id)}
              className="text-xs text-slate-400 transition-colors hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400"
            >
              {t('dashboard.study.history.delete')}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function MobileHistoryCard({
  session: s,
  t,
  dateLocale,
  onResume,
  onDelete,
  onReview,
}: {
  session: SessionSummary;
  t: TFunction;
  dateLocale: string;
  onResume: () => void;
  onDelete: () => void;
  onReview: () => void;
}) {
  const [showConfirm, setShowConfirm] = useState(false);
  const accuracy =
    s.total_answered > 0 ? Math.round((s.correct_count / s.total_answered) * 100) : 0;

  return (
    <Card className="space-y-2 p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="truncate text-sm font-medium text-slate-900 dark:text-white">
          {s.session_name}
        </h3>
        <span
          className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
            s.is_completed
              ? 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300'
              : 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
          }`}
        >
          {s.is_completed ? t('dashboard.study.history.statusCompletedLabel') : t('dashboard.study.history.statusActiveLabel')}
        </span>
      </div>
      <div className="flex flex-wrap gap-2 text-xs text-slate-500 dark:text-slate-400">
        <span className="capitalize">{s.mode === 'exam' ? t('dashboard.study.history.modeExam') : t('dashboard.study.history.modeStudy')}</span>
        <span>
          {t('dashboard.study.sessions.progressOf', { cursor: s.total_answered, total: s.total_questions })}
        </span>
        {s.total_answered > 0 && <span>{accuracy}%</span>}
        <span>{formatDate(s.started_at, dateLocale)}</span>
        {s.time_spent_seconds > 0 && <span>{formatDuration(s.time_spent_seconds)}</span>}
      </div>
      <div className="flex gap-2 pt-1">
        {!s.is_completed && (
          <Button variant="primary" size="sm" onClick={onResume} className="flex-1">
            {t('dashboard.study.history.resume')}
          </Button>
        )}
        {s.is_completed && (
          <Button variant="ghost" size="sm" onClick={onReview} className="flex-1">
            {t('dashboard.study.history.review')}
          </Button>
        )}
        {showConfirm ? (
          <div className="flex gap-1">
            <button
              onClick={onDelete}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700"
            >
              {t('dashboard.study.history.yes')}
            </button>
            <button
              onClick={() => setShowConfirm(false)}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600 transition-colors hover:bg-slate-50 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/5"
            >
              {t('dashboard.study.history.no')}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowConfirm(true)}
            className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-slate-400 transition-colors hover:border-red-300 hover:text-red-500 dark:border-white/10 dark:text-slate-500 dark:hover:border-red-500/40 dark:hover:text-red-400"
          >
            {t('dashboard.study.history.delete')}
          </button>
        )}
      </div>
    </Card>
  );
}
